import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FolderOpen } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ItemCard from "@/components/portfolio/ItemCard";
import { Reveal, ChapterBadge } from "@/components/Reveal";
import { api } from "@/lib/api";
import { useLenis } from "@/lib/scroll";

export default function PortfolioCategory() {
  useLenis();
  const { categorySlug } = useParams();
  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get("/portfolio/categories")
      .then((r) => {
        const cat = r.data.find((c) => c.slug === categorySlug);
        if (!cat) {
          setNotFound(true);
          setLoading(false);
          return null;
        }
        setCategory(cat);
        document.title = `Sahil Paruthi — ${cat.name} Portfolio`;
        return api.get("/portfolio/items", { params: { category: categorySlug, limit: 500 } });
      })
      .then((r) => {
        if (r) setItems(r.data.items);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [categorySlug]);

  return (
    <div className="min-h-screen bg-parchment text-charcoal-900">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Link
              to="/portfolio"
              data-testid="back-to-portfolio"
              className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-copper-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Explore all work
            </Link>
          </Reveal>

          {loading ? (
            <p className="mt-10 text-sm text-charcoal-500 font-mono">Loading...</p>
          ) : notFound ? (
            <div className="mt-10" data-testid="category-not-found">
              <h1 className="font-serif text-3xl text-charcoal-900">Category not found</h1>
              <p className="mt-2 text-charcoal-600">This category may have been unpublished or renamed.</p>
            </div>
          ) : (
            <>
              <div className="mt-8">
                <ChapterBadge number="" label="Portfolio / Category" />
                <Reveal className="mt-6 max-w-3xl">
                  <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-charcoal-900 leading-[1.08]">
                    {category.name}
                  </h1>
                  {category.description && (
                    <p className="mt-4 text-base sm:text-lg text-charcoal-600 leading-relaxed">{category.description}</p>
                  )}
                  <p className="mt-3 font-mono text-xs tracking-[0.2em] uppercase text-charcoal-500">
                    {items.length} {items.length === 1 ? "work" : "works"}
                  </p>
                </Reveal>
              </div>

              <div className="mt-12">
                {items.length === 0 ? (
                  <div
                    data-testid="category-empty-state"
                    className="rounded-2xl border border-dashed border-charcoal-900/20 bg-surface-white/60 p-12 text-center"
                  >
                    <FolderOpen className="w-8 h-8 mx-auto text-copper-600/60" />
                    <p className="mt-4 font-serif text-2xl text-charcoal-900">Work will be added here</p>
                    <p className="mt-2 text-sm text-charcoal-500 max-w-md mx-auto leading-relaxed">
                      This category is ready — new work will appear here as soon as it is published.
                    </p>
                  </div>
                ) : (
                  <div className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="category-grid">
                    {items.map((item) => (
                      <ItemCard key={item.id} item={item} categorySlug={category.slug} categoryName={category.name} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
